import { IconCross } from '@components/icons';
import { Accordion, AccordionItem } from '@components/layout/Accordion';
import { Dependency } from '@modules/Dependencies/api/models/dependency.model';
import React from 'react';
import { fadeInLeft, fadeOutLeft } from 'react-animations';
import styled, { keyframes } from 'styled-components';

import SpinnerFullScreen from '../../../../../components/dependencies/Spinner';
import { OpenStatusEnum } from '../useAppState';

import { GlobalWrapperStyles } from '../utils/sharedStyles';
import { Item } from './Item';
import OutsideClickHandler from 'react-outside-click-handler';

const AnimatedDiv = styled(GlobalWrapperStyles)<{ isManualHide: boolean; isOpen: boolean }>`
    &&& {
        width: 360px;
        top: 71px;
        bottom: 0;
        left: 91px;
        height: calc(100vh - 98px);
        border-radius: 0;
        background-color: #fff;
        border-right: 1px solid #e2e2d3;
        box-shadow: none;
        display: flex;
        flex-direction: column;
        opacity: ${(props) => (props.isManualHide ? '0' : '1')};
        margin-left: ${(props) => (props.isManualHide ? '-335px' : 'initial')};

        animation: 1s ${(props) => animation[props.isOpen ? 'fadeInLeftBig' : 'fadeOutLeft']};
    }
`;

const Styles = styled.div`
    &&&&& {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        padding: 15px;

        .app-accordion {
            .k-panelbar-header {
                cursor: default;

                .k-panelbar-collapse {
                    transform: rotateZ(180deg);
                }

                > .k-animation-container {
                    margin-top: 13px;
                }

                > .k-link {
                    padding: 7px 0;
                    margin: -7px 0;
                    align-items: baseline !important;
                }
            }

            .k-panelbar-item-text {
                font-style: normal;
                font-weight: 500;
                font-size: 10px;
                line-height: 14px;
            }

            .k-panelbar-content.k-panelbar-content.k-panelbar-content {
                border-bottom: 1px solid ${(props) => props.theme.colors.road_grey_100} !important;

                &:first-child {
                    border-top: 1px solid ${(props) => props.theme.colors.road_grey_100} !important;
                }

                &:last-child {
                    margin-bottom: 18px;
                }
            }

            .k-panelbar-group {
                margin-bottom: 14px;
            }
        }

        .close-icon {
            padding: 7px;
            cursor: pointer;
            stroke: #7c86a3;
            align-self: end;
            position: absolute;
            top: 5px;
            right: 5px;
            min-height: 24px;
            min-width: 24px;
            z-index: 1;
        }

        .k-panelbar-item-text {
            font-size: 10px;
            line-height: 14px;
        }

        .main-sections {
            height: 100%;
            overflow: hidden;
            display: flex;
            flex-direction: column;

            > .k-panelbar-header {
                overflow: hidden;
                min-height: 36px;
                display: flex;
                flex-direction: column;

                > .k-animation-container {
                    flex: 1 1 auto;
                    margin-bottom: 5px;

                    > .k-child-animation-container {
                        height: 100%;
                        overflow-x: hidden;
                        overflow-y: auto;

                        &[class*='k-reveal'] {
                            overflow: hidden;
                        }
                    }
                }

                > .k-link > .k-panelbar-toggle {
                    margin-top: -12px;
                }
            }
        }
    }
`;

const animation = {
    fadeInLeftBig: keyframes`${fadeInLeft}`,
    fadeOutLeft: keyframes`${fadeOutLeft}`,
};

export type OpenedMenuProps = {
    handleClose: () => void;
    handleTransition: (item: Dependency) => void;
    recentOpened: Dependency[];
    dependencies: {
        error: Dependency[];
        correct: Dependency[];
        usedIn: Dependency[];
        uses: Dependency[];
    };
    isLoading: boolean;
    openStatus: string;
};

const OpenedMenu = ({
    handleClose,
    handleTransition,
    recentOpened,
    dependencies,
    isLoading,
    openStatus,
}: OpenedMenuProps) => {
    const outsideClick = () => {
        if (openStatus === OpenStatusEnum.OPEN) {
            handleClose();
        }
    };

    const sections = [
        recentOpened.length && (
            <AccordionItem title="Recently opened" key="recent">
                {recentOpened.map((item, index) => (
                    <Item
                        item={item}
                        key={`${item.Name}-${index}`}
                        handleClick={() => {}}
                        showCurrentItemLocation={false}
                    />
                ))}
            </AccordionItem>
        ),

        dependencies?.error?.length && (
            <AccordionItem title="Has errors" key="errors">
                {dependencies.error.map((item, index) => (
                    <Item item={item} key={`${item.Name}-${index}`} handleClick={() => handleTransition(item)} />
                ))}
            </AccordionItem>
        ),

        dependencies.usedIn?.length && (
            <AccordionItem title="Is used in" expanded={true} key="used_in">
                {dependencies.usedIn.map((item, index) => (
                    <Item item={item} key={`${item.Name}-${index}`} handleClick={() => handleTransition(item)} />
                ))}
            </AccordionItem>
        ),

        dependencies.uses?.length && (
            <AccordionItem title="Uses" expanded={true} key="uses">
                {dependencies.uses.map((item, index) => (
                    <Item item={item} key={`${item.Name}-${index}`} handleClick={() => handleTransition(item)} />
                ))}
            </AccordionItem>
        ),
    ].filter((section) => !!section);

    return (
        <OutsideClickHandler onOutsideClick={outsideClick}>
            <AnimatedDiv
                isOpen={openStatus === OpenStatusEnum.OPEN}
                isManualHide={openStatus === OpenStatusEnum.MANUAL_HIDE}
                className="navigation-global-wrapper"
            >
                <Styles>
                    <IconCross className="close-icon" onClick={handleClose} />

                    {sections.length > 0 ? (
                        <Accordion flat expandMode="single" className="main-sections">
                            {sections}
                        </Accordion>
                    ) : (
                        <h6>No dependencies were found.</h6>
                    )}
                </Styles>

                {isLoading && <SpinnerFullScreen />}
            </AnimatedDiv>
        </OutsideClickHandler>
    );
};

export default OpenedMenu;
